<template>
  <div class="compare-container">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
      <p class="mt-3">Vergelijkingsweergave laden...</p>
    </div>

    <!-- Main content -->
    <div v-else-if="compareConfig" class="content">
      <a class="skip-link" href="#hoofdinhoud">Ga naar de projectenlijst</a>

      <!-- Header -->
      <header class="compare-header">
        <div class="container-fluid px-4">
          <!-- flex-wrap is hier geen opmaakkeuze maar SC 1.4.10: zonder wrap
               duwt de knoppengroep de pagina op 320px breed 121px zijwaarts. -->
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h1>{{ compareConfig.name }}</h1>
              <p v-if="compareConfig.description" class="mb-0">{{ compareConfig.description }}</p>
            </div>
            <div class="view-toggle" role="group" aria-label="Weergave kiezen">
              <button type="button" class="btn btn-custom" :class="currentView === 'list' ? 'btn-custom-active' : 'btn-custom-outline'"
                      :aria-pressed="currentView === 'list'" @click="currentView = 'list'">
                <i class="fas fa-list-ul me-1" aria-hidden="true"></i> Lijst
              </button>
              <button type="button" class="btn btn-custom" :class="currentView === 'table' ? 'btn-custom-active' : 'btn-custom-outline'"
                      :aria-pressed="currentView === 'table'" @click="currentView = 'table'">
                <i class="fas fa-table me-1" aria-hidden="true"></i> Tabel
              </button>
              <button type="button" class="btn btn-custom" :class="currentView === 'compare' ? 'btn-custom-active' : 'btn-custom-outline'"
                      :aria-pressed="currentView === 'compare'"
                      @click="currentView = 'compare'"
                      :disabled="selectedRecords.length === 0">
                <i class="fas fa-columns me-1" aria-hidden="true"></i> Vergelijk
                <span class="badge bg-secondary">{{ selectedRecords.length }}</span>
                <span class="visually-hidden">geselecteerde projecten</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="hoofdinhoud" tabindex="-1">

      <!-- Search and Filters -->
      <div class="container-fluid mt-4 px-4">
        <div class="search-section" v-if="compareConfig.searchConfig?.enabled">
          <label class="visually-hidden" for="vergelijk-zoeken">Zoeken in projecten</label>
          <div class="input-group">
            <span class="input-group-text"><i class="fas fa-search" aria-hidden="true"></i></span>
            <input id="vergelijk-zoeken" type="search" class="form-control" v-model="searchQuery" placeholder="Zoeken..." @input="handleSearch">
            <button type="button" class="btn btn-outline-secondary" @click="clearSearch" aria-label="Zoekopdracht wissen">
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <!-- Advanced filters button -->
        <div class="mt-3" v-if="compareConfig.filters && compareConfig.filters.length > 0">
          <button type="button" class="btn btn-custom-outline" @click="showFilters = !showFilters"
                  :aria-expanded="showFilters" aria-controls="vergelijk-filters">
            <i class="fas fa-filter me-1" aria-hidden="true"></i> Geavanceerd zoeken
            <span v-if="hasActiveFilters" class="badge bg-danger ms-1">Actief</span>
          </button>
        </div>

        <!-- Advanced filters panel -->
        <div v-if="showFilters" id="vergelijk-filters" class="filter-section mt-3">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h2 class="h6 mb-0">Geavanceerd zoeken</h2>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="showFilters = false"
                      aria-label="Geavanceerd zoeken sluiten">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div class="card-body">
              <div class="row">
                <div v-for="filter in compareConfig.filters" :key="filter.field" class="col-md-4 mb-3">
                  <!-- Select filter -->
                  <template v-if="filter.type === 'select'">
                    <label class="form-label fw-bold" :for="'filter-' + filter.field">{{ filter.label }}</label>
                    <select :id="'filter-' + filter.field" class="form-select" v-model="activeFilters[filter.field]" @change="applyFilters">
                      <option value="">Alle</option>
                      <option v-for="option in getFilterOptions(filter.field)" :key="option" :value="option">
                        {{ option }}
                      </option>
                    </select>
                  </template>

                  <!-- Checkbox/Radio filter: de groep krijgt zijn naam van de legend,
                       elke optie een eigen gekoppeld label. -->
                  <fieldset v-else-if="filter.type === 'checkbox' || filter.type === 'radio'" class="a11y-fieldset">
                    <legend class="form-label fw-bold">{{ filter.label }}</legend>
                    <div v-for="(option, i) in getFilterOptions(filter.field)" :key="option" class="form-check">
                      <input
                        :id="'filter-' + filter.field + '-' + i"
                        :type="filter.type"
                        class="form-check-input"
                        :name="'filter-' + filter.field"
                        :value="option"
                        v-model="activeFilters[filter.field]"
                        @change="applyFilters"
                      >
                      <label class="form-check-label" :for="'filter-' + filter.field + '-' + i">{{ option }}</label>
                    </div>
                  </fieldset>
                </div>
              </div>
              <div class="mt-3">
                <button class="btn btn-sm btn-outline-secondary" @click="resetFilters">Reset</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Aantal resultaten hoorbaar maken: zoeken en filteren wijzigen de
           tabel zonder dat er iets de focus krijgt. -->
      <p class="visually-hidden" aria-live="polite">
        {{ filteredRecords.length }} van {{ allRecords.length }} projecten getoond.
      </p>

      <!-- List View -->
      <div v-if="currentView === 'list'" class="container-fluid mt-4 px-4">
        <CompareTable 
          :records="filteredRecords"
          :displayFields="compareConfig.displayFields"
          :selectedRecords="selectedRecords"
          :maxSelect="maxSelectColumns"
          :fieldLabels="getFieldLabels()"
          @select="toggleRecordSelection"
        />
      </div>

      <!-- Table Pivot View -->
      <div v-else-if="currentView === 'table'" class="container-fluid mt-4 px-4">
        <CompareTablePivot 
          :records="filteredRecords"
          :displayFields="compareConfig.displayFields"
          :fieldLabels="getFieldLabels()"
        />
      </div>

      <!-- Compare View -->
      <div v-else class="container-fluid mt-4 px-4">
        <CompareColumns 
          :records="getSelectedRecordData()"
          :compareFields="compareConfig.compareFields"
          :fieldLabels="getFieldLabels()"
          @remove="removeFromCompare"
        />
      </div>
      </main>
    </div>

    <!-- Error state -->
    <div v-else class="error-state" role="alert">
      <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
      <h1>Configuratie niet gevonden</h1>
      <p>De opgevraagde vergelijkingsweergave kon niet worden geladen.</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ServiceFactory from '../services/ServiceFactory';
import CompareTable from '../components/compare/CompareTable.vue';
import CompareColumns from '../components/compare/CompareColumns.vue';
import CompareTablePivot from '../components/compare/CompareTablePivot.vue';

export default defineComponent({
  name: 'CompareView',
  components: {
    CompareTable,
    CompareColumns,
    CompareTablePivot
  },
  data() {
    return {
      loading: true,
      compareConfig: null as any,
      formStructure: null as any,
      allRecords: [] as any[],
      filteredRecords: [] as any[],
      selectedRecords: [] as string[],
      searchQuery: '',
      currentView: 'list' as 'list' | 'compare' | 'table',
      showFilters: false,
      activeFilters: {} as Record<string, any>,
    };
  },
  computed: {
    hasActiveFilters(): boolean {
      return Object.values(this.activeFilters).some(v => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== '' && v !== null && v !== undefined;
      });
    },
    maxSelectColumns(): number {
      // Zorg altijd voor minimaal 20 kolommen, ongeacht de config
      const configMax = this.compareConfig?.maxCompareColumns || 4;
      return Math.max(configMax, 20);
    }
  },
  methods: {
    async loadConfig() {
      try {
        const configId = this.$route.params.id as string;
        const service = ServiceFactory.getCompareService();
        
        // Load config and data in parallel
        [this.compareConfig, this.allRecords, this.formStructure] = await Promise.all([
          service.getCompareConfig(configId),
          service.getCompareData(configId),
          service.getFormStructure(configId)
        ]);

        this.filteredRecords = [...this.allRecords];
      } catch (error) {
        console.error('Error loading compare view:', error);
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.applyFilters();
    },

    clearSearch() {
      this.searchQuery = '';
      this.applyFilters();
    },

    applyFilters() {
      let filtered = [...this.allRecords];

      // Apply search
      if (this.searchQuery && this.compareConfig.searchConfig?.searchableFields) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(record => {
          return this.compareConfig.searchConfig.searchableFields.some((field: string) => {
            const value = record[field];
            return value && String(value).toLowerCase().includes(query);
          });
        });
      }

      // Apply advanced filters
      Object.entries(this.activeFilters).forEach(([field, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return;

        filtered = filtered.filter(record => {
          const recordValue = record[field];
          
          if (Array.isArray(value)) {
            // Checkbox filter (multi-select)
            return value.some(v => {
              if (Array.isArray(recordValue)) {
                return recordValue.includes(v);
              }
              return recordValue === v;
            });
          } else {
            // Select/Radio filter (single)
            return recordValue === value;
          }
        });
      });

      this.filteredRecords = filtered;
    },

    resetFilters() {
      this.activeFilters = {};
      this.searchQuery = '';
      this.applyFilters();
    },

    toggleRecordSelection(recordId: string) {
      const index = this.selectedRecords.indexOf(recordId);
      if (index > -1) {
        this.selectedRecords.splice(index, 1);
      } else {
        if (this.selectedRecords.length < this.maxSelectColumns) {
          this.selectedRecords.push(recordId);
        }
      }
    },

    removeFromCompare(recordId: string) {
      const index = this.selectedRecords.indexOf(recordId);
      if (index > -1) {
        this.selectedRecords.splice(index, 1);
      }
    },

    getSelectedRecordData() {
      return this.selectedRecords.map(id => 
        this.allRecords.find(r => r.id === id)
      ).filter(Boolean);
    },

    getFilterOptions(field: string) {
      // Extract unique values from records for this field
      const values = new Set();
      this.allRecords.forEach(record => {
        const value = record[field];
        if (Array.isArray(value)) {
          value.forEach(v => values.add(v));
        } else if (value) {
          values.add(value);
        }
      });
      return Array.from(values);
    },

    getFieldLabels(): Record<string, string> {
      const labels: Record<string, string> = {};
      
      if (!this.formStructure || !this.formStructure.pages) {
        return labels;
      }

      this.formStructure.pages.forEach((page: any) => {
        page.elements?.forEach((element: any) => {
          if (element.name) {
            labels[element.name] = element.label?.nl || element.name;
          }
        });
      });

      return labels;
    }
  },
  mounted() {
    this.loadConfig();
  }
});
</script>

<style scoped>
.compare-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  text-align: center;
}

/* Skip-link: onzichtbaar tot hij focus krijgt, dan bovenaan in beeld.
   Niet met display:none verbergen — dan is hij ook niet focusbaar. */
.skip-link {
  /* fixed en boven alles: met absolute schuift een paneel er zo overheen en
     zie je de link niet, terwijl hij wel focus heeft. */
  position: fixed;
  left: -9999px;
  top: 0;
  z-index: 10000000;
  background: var(--primary, #0f6c65);
  color: #fff;
  padding: 0.6rem 1rem;
  text-decoration: none;
}
.skip-link:focus {
  left: 0;
  top: 0;
  outline: 3px solid #ffd23f;
  outline-offset: -3px;
}

/* Zichtbare landing na de skip-link; zonder ring lijkt het alsof er niets
   gebeurt. :focus-visible houdt de ring weg bij een muisklik. */
#hoofdinhoud:focus-visible {
  outline: 3px solid var(--primary, #0f6c65);
  outline-offset: -3px;
}

/* Een fieldset geeft de groep zijn naam via de legend, maar brengt eigen
   rand/marge mee die niet bij de Bootstrap-opmaak past. */
.a11y-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-inline-size: 0;
}
.a11y-fieldset > legend {
  float: none;
  width: auto;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.error-state i {
  font-size: 4rem;
  color: #dc3545;
  margin-bottom: 1rem;
}

.compare-header {
  background-color: var(--primary, #0f6c65);
  color: white;
  padding: 1.5rem 0;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.compare-header h1 {
  color: white;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.compare-header p {
  color: rgba(255, 255, 255, 0.9);
}

.view-toggle {
  background: white;
  border-radius: 8px;
  padding: 0.5rem;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.view-toggle .btn {
  border-radius: 6px;
}

.search-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.filter-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-custom-active {
  background-color: var(--primary, #0f6c65);
  border-color: var(--primary, #0f6c65);
  color: white;
}

.btn-custom-active:hover {
  /* Een donkerder tint van het klantpalet in plaats van een vaste kleur:
     die zou bij elke andere huisstijl terugvallen op het oude groen. */
  filter: brightness(0.88);
  color: white;
}

.btn-custom-outline {
  background-color: white;
  border-color: var(--primary, #0f6c65);
  color: var(--primary, #0f6c65);
}

.btn-custom-outline:hover {
  background-color: var(--primary, #0f6c65);
  border-color: var(--primary, #0f6c65);
  color: white;
}

.btn-custom-outline:disabled {
  background-color: white;
  border-color: #dee2e6;
  color: #6c757d;
  opacity: 0.65;
  cursor: not-allowed;
}
</style>









